/**
 * 出金履歴年タブ管理
 *
 * initializePayoutRequestHistoryTabs    - 履歴タブの初期化（connect時に呼び出し）
 * selectPayoutRequestHistoryYear        - タブクリック時のハンドラー
 * payoutRequestHistoryYearsValueChanged - Turbo Stream更新後のタブ再描画
 * setPayoutRequestHistoryViewState      - データ/空コンテナの表示切り替え
 * updatePayoutRequestHistoryTabStyles   - 選択状態のスタイル更新
 * renderPayoutRequestHistoryTabs        - 年タブボタンのDOM構築
 * updatePayoutRequestHistoryFrameSrc    - Turbo Frameのsrc設定
 */

export const PayoutRequestHistoryTabsManagementMixin = {
  /**
   * 履歴タブの初期化
   * connect() から呼び出される
   */
  initializePayoutRequestHistoryTabs() {
    const years = this.payoutRequestHistoryYearsValue || [];

    // No years - show empty container
    if (years.length === 0) {
      this.setPayoutRequestHistoryViewState("empty");
      this._payoutRequestHistoryTabsInitialized = true;
      return;
    }

    // Set default selected year if not set
    if (!this.selectedPayoutRequestHistoryYearValue) {
      this.selectedPayoutRequestHistoryYearValue = years[0];
    }

    // Render tabs and set frame src
    this.renderPayoutRequestHistoryTabs();
    this.updatePayoutRequestHistoryFrameSrc(this.selectedPayoutRequestHistoryYearValue);
    this.setPayoutRequestHistoryViewState("data");
    this._payoutRequestHistoryTabsInitialized = true;
  },

  /**
   * タブクリック時のハンドラー
   * data-action="click->harvest-warehouse#selectPayoutRequestHistoryYear"
   */
  selectPayoutRequestHistoryYear(event) {
    const year = parseInt(event.currentTarget.dataset.year, 10);
    if (year === this.selectedPayoutRequestHistoryYearValue) return;

    this.selectedPayoutRequestHistoryYearValue = year;
    this.updatePayoutRequestHistoryTabStyles(year);
    this.updatePayoutRequestHistoryFrameSrc(year);
  },

  /**
   * Turbo Stream で年リストが更新された時のコールバック
   * 出金申請（新しい年が追加に限る）またはキャンセル（年が削除に限る）された場合にタブを再描画
   *
   * SPA構成のため、出金申請画面と履歴画面はCSS表示切り替えで共存しており、
   * connect()は初回のみ実行される。そのため、セッション中の年リスト変化には
   * このコールバックで対応する必要がある。
   */
  payoutRequestHistoryYearsValueChanged() {
    // Skip the connect-time invocation (initializePayoutRequestHistoryTabs handles it)
    if (!this._payoutRequestHistoryTabsInitialized) return;

    const years = this.payoutRequestHistoryYearsValue || [];

    if (years.length === 0) {
      this.setPayoutRequestHistoryViewState("empty");
      return;
    }

    // Preserve selected year if still valid, otherwise default to first
    if (!years.includes(this.selectedPayoutRequestHistoryYearValue)) {
      this.selectedPayoutRequestHistoryYearValue = years[0];
      this.updatePayoutRequestHistoryFrameSrc(this.selectedPayoutRequestHistoryYearValue);
    }

    this.renderPayoutRequestHistoryTabs();
    this.setPayoutRequestHistoryViewState("data");
  },

  /**
   * データ有無に応じてdata-container/empty-containerを切り替え
   * @param {string} state - "data" または "empty"
   */
  setPayoutRequestHistoryViewState(state) {
    const showData = state === "data";

    if (this.hasPayoutRequestHistoryDataContainerTarget) {
      this.payoutRequestHistoryDataContainerTarget.style.display = showData ? "" : "none";
    }
    if (this.hasPayoutRequestHistoryEmptyContainerTarget) {
      this.payoutRequestHistoryEmptyContainerTarget.style.display = showData ? "none" : "";
    }
  },

  /**
   * 選択状態のスタイル更新
   * @param {number} selectedYear - 選択された年
   */
  updatePayoutRequestHistoryTabStyles(selectedYear) {
    this.payoutRequestHistoryTabTargets.forEach((tab) => {
      const year = parseInt(tab.dataset.year, 10);
      tab.toggleAttribute("data-selected", year === selectedYear);
    });
  },

  /**
   * 年タブボタンのDOM構築
   * <template>内の<button>を雛形として、年ごとにコピーしてタブを組み立てる
   */
  renderPayoutRequestHistoryTabs() {
    const years = this.payoutRequestHistoryYearsValue || [];

    if (
      !this.hasPayoutRequestHistoryTabTemplateTarget ||
      !this.hasPayoutRequestHistoryTabsListTarget
    )
      return;

    const template = this.payoutRequestHistoryTabTemplateTarget;
    const tabsContainer = this.payoutRequestHistoryTabsListTarget;

    // Clear existing tabs
    tabsContainer.innerHTML = "";

    // Clone template for each year
    years.forEach((year) => {
      const tab = template.content.firstElementChild.cloneNode(true);
      tab.textContent = year;
      tab.dataset.year = year;

      if (year === this.selectedPayoutRequestHistoryYearValue) {
        tab.setAttribute("data-selected", "");
        requestAnimationFrame(() => {
          tab.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
          });
        });
      }

      tabsContainer.appendChild(tab);
    });
  },

  /**
   * Turbo Frameのsrcに特定のyearを指定し、その年の出金申請履歴のHTMLを取得する
   * （src変更でTurbo Frameが自動的にGETリクエストを送り、コンテンツを差し替える）
   * @param {number} year - 表示する年
   */
  updatePayoutRequestHistoryFrameSrc(year) {
    if (!this.hasPayoutRequestHistoryFrameTarget || !this.payoutRequestHistoryPathValue) return;

    const params = new URLSearchParams({ year });
    this.payoutRequestHistoryFrameTarget.src = `${this.payoutRequestHistoryPathValue}?${params}`;
  },
};
